import { useCallback, useMemo, useRef } from "react";

import {
  addOrUpdateKeyInOmnisearch,
  breakApartOmnisearch,
  removeKeyInOmnisearch,
} from "../../utils/omnisearchUtils";
import { useOmnisearchStore, useOmnisearchActions, FilterKeys } from "./omnisearchStore";
import { useLocalOmnisearch } from "./useLocalOmnisearch";

export interface UseOmnisearchStateOptions {
  /**
   * Whether to use URL-driven state (default: true).
   *
   * **Must be constant for the lifetime of the component instance.** A change
   * between renders flips which underlying hooks are invoked, which violates
   * React's rules of hooks. Pick the mode at mount time and stick with it.
   */
  isURLDriven?: boolean;
  /** Initial omnisearch value for local mode */
  initialValue?: string;
  /** Initial filters for local mode */
  initialFilters?: { field: string; value: string | boolean }[];
}

export interface UseOmnisearchStateResult {
  /** Current omnisearch string (reactive - causes re-renders when changed) */
  omnisearch: string;
  /** Parsed filter keys for queries */
  filterKeys: FilterKeys;
  /**
   * Get current omnisearch without subscribing to changes.
   * Like Zustand's getState() - stable function that reads current value.
   * Use in callbacks/effects that need current value but shouldn't re-render on changes.
   *
   * @example
   * // In a stable callback that shouldn't depend on omnisearch:
   * const handleExport = useCallback(() => {
   *   const currentSearch = getOmnisearch();
   *   exportData({ search: currentSearch });
   * }, [getOmnisearch]); // getOmnisearch is stable, won't cause re-renders
   */
  getOmnisearch: () => string;
  /** Set omnisearch value (debounced for typing) */
  setOmnisearch: (value: string) => void;
  /** Set omnisearch value immediately (for programmatic updates) */
  setOmnisearchImmediate: (value: string) => void;
  /** Get a specific key value from omnisearch */
  getOmnisearchValue: (key: string) => string | undefined;
  /** Edit multiple key-value pairs in omnisearch */
  editOmnisearchKeyValues: (keyValues: [string, string][]) => void;
  /** Remove a key from omnisearch */
  removeOmnisearchKeyValue: (key: string) => void;
  /** Create a URL with the given key-values applied to omnisearch */
  createUrlFromOmnisearchKeyValues: (params: {
    keyValues: [string, string][];
    keysToKeep?: string[];
  }) => string;
  /** Whether this hook is using URL-driven state */
  isURLDriven: boolean;
}

/**
 * URL-mode implementation. All URL hooks called here, never elsewhere.
 */
function useUrlOmnisearchState(): UseOmnisearchStateResult {
  const urlStore = useOmnisearchStore();
  const storeActions = useOmnisearchActions();

  // Ref pattern keeps callbacks stable while still reading the current value.
  const omnisearchRef = useRef(urlStore.omnisearch);
  omnisearchRef.current = urlStore.omnisearch;

  const getOmnisearch = useCallback(() => omnisearchRef.current, []);

  const getOmnisearchValue = useCallback((key: string): string | undefined => {
    const { kvpValues } = breakApartOmnisearch(omnisearchRef.current);
    const kvp = kvpValues.find(([k]) => k === key);
    return kvp?.[1];
  }, []);

  const editOmnisearchKeyValues = useCallback(
    (keyValues: [string, string][]) => {
      let current = omnisearchRef.current;
      for (const [key, value] of keyValues) {
        current = addOrUpdateKeyInOmnisearch(current, key, value);
      }
      storeActions.setOmnisearchImmediate(current);
    },
    [storeActions]
  );

  const removeOmnisearchKeyValue = useCallback(
    (key: string) => {
      storeActions.setOmnisearchImmediate(removeKeyInOmnisearch(omnisearchRef.current, key));
    },
    [storeActions]
  );

  const createUrlFromOmnisearchKeyValues = useCallback(
    (params: { keyValues: [string, string][]; keysToKeep?: string[] }) => {
      const { keyValues, keysToKeep } = params;
      let result = "";

      if (keysToKeep) {
        const { kvpValues } = breakApartOmnisearch(omnisearchRef.current);
        for (const keyToKeep of keysToKeep) {
          const existingKvp = kvpValues.find(([k]) => k === keyToKeep);
          if (existingKvp) {
            result = addOrUpdateKeyInOmnisearch(result, existingKvp[0], existingKvp[1]);
          }
        }
      } else {
        result = omnisearchRef.current;
      }

      for (const [key, value] of keyValues) {
        result = addOrUpdateKeyInOmnisearch(result, key, value);
      }

      // Preserve every other query param already in the URL — only the
      // `omnisearch` slot is rewritten. The previous implementation rebuilt
      // the URL with just `omnisearch=…`, silently dropping siblings like
      // `?tab=overview` or `?ref=…`.
      const urlParams = new URLSearchParams(window.location.search);
      if (result.length) {
        urlParams.set("omnisearch", result);
      } else {
        urlParams.delete("omnisearch");
      }
      const queryString = urlParams.toString();
      return `${window.location.protocol}//${window.location.host}${
        window.location.pathname
      }${queryString ? `?${queryString}` : ""}`;
    },
    []
  );

  return useMemo(
    () => ({
      omnisearch: urlStore.omnisearch,
      filterKeys: urlStore.filterKeys,
      getOmnisearch,
      setOmnisearch: storeActions.setOmnisearch,
      setOmnisearchImmediate: storeActions.setOmnisearchImmediate,
      getOmnisearchValue,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      createUrlFromOmnisearchKeyValues,
      isURLDriven: true,
    }),
    [
      urlStore.omnisearch,
      urlStore.filterKeys,
      storeActions,
      getOmnisearch,
      getOmnisearchValue,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      createUrlFromOmnisearchKeyValues,
    ]
  );
}

/**
 * Local-mode implementation. All local hooks called here, never elsewhere.
 */
function useLocalOmnisearchState(
  options: Pick<UseOmnisearchStateOptions, "initialValue" | "initialFilters">
): UseOmnisearchStateResult {
  const localState = useLocalOmnisearch({
    initialValue: options.initialValue,
    initialFilters: options.initialFilters,
  });

  return useMemo(
    () => ({
      ...localState,
      isURLDriven: false,
    }),
    [localState]
  );
}

/**
 * Unified omnisearch state hook that works with or without a provider.
 *
 * Picks URL-driven or local mode based on `options.isURLDriven`. **The mode
 * is captured on first render and cannot change** — flipping mid-lifetime
 * would change which set of underlying hooks gets called and violate React's
 * rules of hooks. Dev builds throw if the flag flips.
 *
 * Architecture:
 * - URL-driven (default): Uses useSyncExternalStore to read from URL
 * - Local mode: Uses useState for isolated state management (e.g., modals)
 *
 * Key design principle: Works identically with or without a provider.
 * No conditional logic needed in consuming components.
 *
 * Benefits:
 * - Single source of truth (URL for URL mode, state for local mode)
 * - No bidirectional effects
 * - Consistent API regardless of mode
 * - Stable action references
 * - Referentially stable filterKeys for memoization
 *
 * @example
 * // URL-driven mode (default) - works anywhere
 * const { omnisearch, setOmnisearch, filterKeys } = useOmnisearchState();
 *
 * // Local mode for modals
 * const { omnisearch, setOmnisearch } = useOmnisearchState({ isURLDriven: false });
 */
export function useOmnisearchState(
  options: UseOmnisearchStateOptions = {}
): UseOmnisearchStateResult {
  const { isURLDriven = true, initialValue, initialFilters } = options;

  // Capture the mode on first render; dev builds throw if it ever changes,
  // because flipping the dispatch below would invoke a different hook chain
  // and break the rules of hooks.
  const initialMode = useRef(isURLDriven);
  if (process.env.NODE_ENV !== "production" && initialMode.current !== isURLDriven) {
    throw new Error(
      "useOmnisearchState: `isURLDriven` must stay constant for the lifetime of the component instance."
    );
  }

  // Safe: `initialMode.current` is fixed on first render, so the same branch
  // (and therefore the same hook chain) runs every render.
  return initialMode.current
    ? useUrlOmnisearchState()
    : useLocalOmnisearchState({ initialValue, initialFilters });
}

// Re-export types for convenience
export type { FilterKeys };
